import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteButton from '../components/DeleteButton';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Checkbox, ClickAwayListener, Divider, Grid, IconButton, InputAdornment, Paper, Popper, TextField, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import { Prompt } from 'react-router';

import { DataAPIContext, GET_ONE } from '../api';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import RecordListLight from '../components/RecordListLight';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
import CheckboxOptionsSelect from '../pretrip/CheckboxOptionsSelect';
import { ContextStore } from '../store/contextStore';
import { asJSONPath } from '../utils';
import { useIsWidthUp } from '../utils';
import { debounce } from '../utils';
import PreTripItem, { ARROWS_GROUP_WIDTH } from './TripItemEdit';
import { validateItem } from './utils';
import useConfirmDialog from '../components/useConfirmDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        border: 'solid 1px #D8D8D8',
        margin: '0 1rem',
        paddingTop: '1rem',
        borderRadius: '0 0 12px 12px',

        height: `calc(${theme.cp.contentHeight} - 9rem)`,
        marginBottom: '1rem',
        overflow: 'auto',
        [theme.breakpoints.down('sm')]: {
            paddingBottom: '4rem',
            border: 0,
            margin: 0,
            height: `calc(${theme.cp.contentHeight} - 15.5rem)`,
        },
    },
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 20rem) !important`,

        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 17rem) !important`,
        },
    },
}));

const emptyItem = {
    id: null,
    all_users: true,
    item: null,
    required: false,
    roles: [],
    sequence: 1,
    users: [],
};

const filterUniqueObjects = (objects) => {
    const unique = new Map();

    objects.forEach((obj) => {
        if (!unique.has(obj.item.id)) {
            unique.set(obj.item.id, obj);
        }
    });

    return Array.from(unique.values());
};
const AddItemsButtons = (props) => {
    const { setItemsRecordListSelectAnchorEl, trip, setTripSelectAnchorEl, selectedCount, onDeleteSelected } = props;
    const isSmallScreen = !useIsWidthUp('sm');
    let empty_list_view = !trip.pre_trip_items?.length > 0 && !isSmallScreen;
    return (
        <div style={{ position: empty_list_view ? 'inline-block' : 'fixed', top: empty_list_view ? '40vh' : isSmallScreen ? '4.5rem' : '0.75rem', right: empty_list_view ? 'calc(50vw - 10rem)' : isSmallScreen ? '0.5rem' : '1rem', paddingLeft: '1rem' }}>
            {!isSmallScreen && (
                <Button sx={{ marginRight: '1rem' }} onClick={() => setTripSelectAnchorEl(true)} variant="outlined">
                    Import
                </Button>
            )}

            <Button onClick={() => setItemsRecordListSelectAnchorEl(true)} variant="contained" color="primary">
                <AddIcon fontSize="small"/>&nbsp;Add Trip Items
            </Button>

            {/* Bulk selection is desktop-only */}
            {!isSmallScreen && selectedCount > 0 && (
                <DeleteButton sx={{ marginLeft: '1rem' }} onClick={onDeleteSelected} size="small">
                    Delete selected ({selectedCount})
                </DeleteButton>
            )}
        </div>
    );
};

var isPromptCalled = false;

const handlePromptCalled = () => {
    isPromptCalled = true;
    return 'Are you sure you want to cancel changes?';
}

// CP-8423: "Add item to trip (N)" -- the count only once something is selected. The import
// popper passes 0: it selects source trips, and their count would read as an item count.
const addItemsLabel = (count) => (count > 0 ? `Add item to trip (${count})` : 'Add item to trip');

const clearPromptOnClickaway = () => {
    isPromptCalled = false;
} 

const ImportItemsFromTripPopper = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const { tripSelectAnchorEl, setTripSelectAnchorEl, selectedTrips, setSelectedTrips, setSyncActive, onTripSave } = props;

    const handleCancel = (ev) => {
        if (selectedTrips.length>0 && !window.confirm('Are you sure you want to cancel changes?')) return;
        setSelectedTrips([]);
        setIsNoneSelected(true);
        setTripSelectAnchorEl(null);
    };

    const handleSelectTrip = (ev, trips) => {
        setSelectedTrips(trips);
        setIsNoneSelected(trips.length < 1);
    };

    const handleAddTripItemsToTrip = async () => {
        setSyncActive(true);
        let new_trip_items = [];

        const fetchTripItems = async (trip) => {
            const res = await dataAPI(GET_ONE, 'tour', { id: trip.id });
            if (res.statusCode === 200) {
                let empty_item = {
                    id: null,
                    all_users: true,
                    item: {
                        id: null,
                        item_type: {
                            name: '',
                        },
                    },
                    required: false,
                    roles: [],
                    sequence: null,
                    users: [],
                    name: '',
                };

                let res_items = [];
                for (let index = 0; index < res.data.pre_trip_items?.length; index++) {
                    res_items = [...res_items, { ...empty_item, item: { id: res.data.pre_trip_items[index].item.id } }];
                }
                new_trip_items = [...new_trip_items, ...res_items];
            }
        };
        for (let index = 0; index < selectedTrips?.length; index++) {
            await fetchTripItems(selectedTrips[index]);
        }

        new_trip_items = filterUniqueObjects([...props.trip.pre_trip_items, ...new_trip_items]);
        await onTripSave(new_trip_items);
        setSyncActive(false);
        setTripSelectAnchorEl(null);
        setSelectedTrips([]);
    };

    const [isNoneSelected, setIsNoneSelected] = React.useState(true);

    return (
        <Popper open={Boolean(tripSelectAnchorEl)} style={{ zIndex: 1 }} anchorEl={tripSelectAnchorEl} onClose={() => setTripSelectAnchorEl(null)}>
        <Prompt when={selectedTrips.length>0} message={handlePromptCalled}/>
            <ClickAwayListener
                onClickAway={() => {
                    if (!isPromptCalled) {
                        handleCancel();
                    } else {
                        clearPromptOnClickaway();
                    }
                }}
            >
                <Paper style={{ position: 'fixed', top: '10vh', left: 0, right: 0, margin: 'auto', border: 'solid 1px #8d8d8d', padding: '3rem 2rem', borderRadius: '12px', maxWidth: '40vw' }}>
                    <Typography fontWeight={500} mb={4}>
                        ADD TO TRIP
                    </Typography>
                    <Typography>From what trip do you want to import trip items?</Typography>
                    <RemoteAutocomplete
                        filterSelected
                        InputProps={{ required: true }}
                        multiple
                        resource="tour"
                        label="Select Trip:"
                        variant="filled"
                        size="small"
                        onChange={handleSelectTrip}
                        value={selectedTrips}
                        getOptionLabel={(opt) => {
                            if (opt.is_bespoke) {
                                return '[id:' + opt.id + '] ' + opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                            }
                            return '[id:' + opt.id + '] ' + opt.name + ' (Recurring)';
                        }}
                        renderOption={(props, option) => {
                            if (option.is_bespoke) {
                                return (
                                    <div {...props}>
                                        [id:{option.id}] {option.name} ({option.start_date} - {option.end_date})
                                    </div>
                                );
                            }
                            return (
                                <div {...props}>
                                    [id:{option.id}] {option.name} (Recurring)
                                </div>
                            );
                        }}
                    />
                    <FormActionButtons
                        size="medium"
                        sx={{ mt: 2 }}
                        onCancel={handleCancel}
                        onSubmit={handleAddTripItemsToTrip}
                        submitDisabled={isNoneSelected}
                        submitLabel={addItemsLabel(0)}
                    />
                </Paper>
            </ClickAwayListener>
        </Popper>
    );
};

const ImportItemsRecordListPopper = (props) => {
    const { ItemsRecordListSelectAnchorEl, setItemsRecordListSelectAnchorEl, selectedItems, setSelectedItems, trip, onItemAdd, onTripSave, setSyncActive } = props;
    const formLicensee = React.useContext(FormLicenseeContext);
    const licenseeId = formLicensee || toLicenseeId(trip?.tour_operator);
    const resource = 'pre_trip_item';
    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('sm');
    const searchQueryRef = React.useRef(null);
    const [query, setQuery] = React.useState(null);
    const [recordListFilters, setRecordListFilters] = React.useState(null);

    const [prevQuery, setPrevQuery] = React.useState(query);
    const [prevFilters, setPrevFilters] = React.useState(recordListFilters);

    const filtersChanged = query !== prevQuery || recordListFilters !== prevFilters;

    React.useEffect(() => {
        setPrevQuery(query);
        setPrevFilters(recordListFilters);
    }, [query, recordListFilters]);

    const handleAddItemsToTrip = async () => {
        setSyncActive(true);
        for (let index = 0; index < selectedItems.length; index++) {
            await onItemAdd(selectedItems[index]);
        }
        onHidePopper(null, true);
        await onTripSave(trip.pre_trip_items);
        setSyncActive(false);
    };

    const handleSearchQueryChange = (ev) => {
        debouncedSearchQueryChangeHandler.cancel();
        doSearch(searchQueryRef.current?.value);
    };

    const debouncedSearchQueryChangeHandler = debounce(handleSearchQueryChange, 300);

    const doSearch = (query) => setQuery(query);

    const onHidePopper = (ev, isItemsAdded) => {
        if (!isItemsAdded && (selectedItems.length>0 || recordListFilters) && !window.confirm('Are you sure you want to cancel changes?')) return;
        setItemsRecordListSelectAnchorEl(null);
        setQuery(null);
        setRecordListFilters(null);
        setSelectedItems([]);
    };
    return (
        <Popper open={Boolean(ItemsRecordListSelectAnchorEl)} style={{ zIndex: 1 }} anchorEl={ItemsRecordListSelectAnchorEl} onClose={() => onHidePopper()}>
            <Prompt when={selectedItems.length>0 || recordListFilters} message={handlePromptCalled}/>
            <Paper style={{ position: 'fixed', top: '10vh', left: 0, right: 0, margin: 'auto', border: 'solid 1px #8d8d8d', borderRadius: '12px', maxWidth: isSmallScreen ? '100vw' : '40vw' }}>
                <Grid container justifyContent="space-between" alignItems="center" px={2} py={1} style={{ backgroundColor: '#F5F5F5', borderRadius: '12px 12px 0px 0px' }}>
                    <Grid item>
                        <Typography fontWeight={500}>Add trip items</Typography>
                    </Grid>
                    <Grid item>
                        <Tooltip title="Close">
                            <IconButton onClick={onHidePopper}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Grid container>
                    <Grid item xs={6} p={2}>
                            <TextField
                                variant="filled"
                                label="Search"
                                size="small"
                                onChange={debouncedSearchQueryChangeHandler}
                                inputRef={searchQueryRef}
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton aria-label="do search" onClick={handleSearchQueryChange}>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    {!props.isSmallScreen && (<Grid item xs={6} p={2}>
                        <CheckboxOptionsSelect recordListFilters={recordListFilters} setRecordListFilters={setRecordListFilters} />
                    </Grid>)}
                    <RecordListLight
                        isFilterChanged={filtersChanged}
                        className={classes.recordList}
                        apiFilters={[
                            { field: 'item_type', value: recordListFilters },
                            ...(licenseeId ? [{ field: 'tour_operator', value: licenseeId }] : []),
                        ]}
                        resource={resource}
                        disableSelectionOnClick={false}
                        selectionModel={selectedItems}
                        onRowSelectionModelChange={(newRowSelectionModel) => {
                            setSelectedItems(newRowSelectionModel);
                        }}
                        rowSelectionModel={selectedItems}                        
                        query={query}
                        pageSizeOptions={[10, 25, 50, 100]}
                        perPage={10}
                        disableColumnMenu={true}
                        disableListBorder={false}
                        limitHeightForMobile={'calc(100vh - 26rem)'}
                        columns={[
                            { field: 'name', headerName: 'Name', sortable: false, flex: 1 },
                            // CP-6187: what guests see in the app (blank: the Name). Search finds it too.
                            { field: 'friendly_name', headerName: 'Friendly Name', sortable: false, flex: 1, valueGetter: (p) => p.row.friendly_name || '' },
                            { field: 'type', headerName: 'Category', sortable: false, flex: 0.5, valueGetter: (p) => (p.row.item_type ? p.row.item_type.name : '[not set]') },
                        ]}
                    />
                    {/* Own full-width row, under the list rather than beside it. */}
                    <Grid item xs={12} p={2}>
                        <FormActionButtons
                            size="medium"
                            onCancel={onHidePopper}
                            onSubmit={handleAddItemsToTrip}
                            submitLabel={addItemsLabel(selectedItems.length)}
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Popper>
    );
};

const TripFlowPreTripList = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    // const classes = useStyles();
    const items = props.trip.pre_trip_items || [];
    const { hasPerm } = React.useContext(ContextStore);
    // const { hasPerm } = React.useContext(ContextStore);
    const [tripSelectAnchorEl, setTripSelectAnchorEl] = React.useState(null);
    const [ItemsRecordListSelectAnchorEl, setItemsRecordListSelectAnchorEl] = React.useState(null);
    const [selectedTrips, setSelectedTrips] = React.useState([]);
    const [selectedItems, setSelectedItems] = React.useState([]);
    const [selectedIdxs, setSelectedIdxs] = React.useState([]);
    const isSmallScreen = !useIsWidthUp('sm');
    const hasWritePerm = hasPerm('tour', 'write')
    // Bulk selection/delete is desktop-only.
    const bulkSelectEnabled = hasWritePerm && !isSmallScreen;

    // Indices shift whenever items are added/imported/deleted — drop the selection to avoid deleting the wrong rows.
    React.useEffect(() => {
        setSelectedIdxs([]);
    }, [items.length]);

    React.useEffect(() => {
        if (!bulkSelectEnabled) {
            setSelectedIdxs([]);
        }
    }, [bulkSelectEnabled]);

    const onItemChange = (item, idx) => {
        props.onChange && props.onChange(asJSONPath(`pre_trip_items[${idx}]`, item));
    };

    const onItemDelete = (item, idx) => {
        const path = `pre_trip_items[${idx}]`;
        props.onDelete && props.onDelete(idx);
        props.onValidate && props.onValidate(path, null);
    };
    const onSelectChange = (idx, checked) => {
        setSelectedIdxs((prev) => (checked ? [...prev.filter((i) => i !== idx), idx] : prev.filter((i) => i !== idx)));
    };

    const onToggleSelectAll = (ev) => {
        setSelectedIdxs(ev.target.checked ? items.map((x, i) => i) : []);
    };

    const onSelectedDelete = async () => {
        if (!selectedIdxs.length) return;
        if (!(await confirmDelete({ body: `Are you sure you want to delete ${selectedIdxs.length} selected trip item${selectedIdxs.length > 1 ? 's' : ''}?` }))) return;

        const removed = new Set(selectedIdxs);
        const remaining = items.filter((x, i) => !removed.has(i));

        props.onDeleteMany && props.onDeleteMany(selectedIdxs);

        // Validation state is keyed by index, so re-key it for the items that stayed.
        if (props.onValidate) {
            items.forEach((x, i) => props.onValidate(`pre_trip_items[${i}]`, null));
            remaining.forEach((item, i) => {
                const { canSave, errors } = validateItem(item);
                props.onValidate(`pre_trip_items[${i}]`, canSave, errors);
            });
        }
        setSelectedIdxs([]);
    };

    const onItemAdd = (newItemId) => {
        // let items = prevItems || items;
        const maxseq = items.reduce((a, i) => Math.max(a, +i.sequence), Number.NEGATIVE_INFINITY);
        const nextseq = isNaN(maxseq) || maxseq === Number.NEGATIVE_INFINITY ? 1 : maxseq + 1;
        console.log('maxseq', maxseq, nextseq, items.length);
        const path = `pre_trip_items[${items.length}]`;
        let item = { ...emptyItem, sequence: nextseq };
        if (newItemId) {
            item.item = {};
            item.item.id = newItemId;
            item.item.item_type = {};
            item.item.item_type.name = '';
            // item.item.name = '';
            item.name = '';
        }
        const { canSave, errors } = validateItem(item);
        console.log('onItemAdd', path, item, canSave);
        props.onChange && props.onChange(asJSONPath(path, item));
        props.onValidate && props.onValidate(path, canSave, errors);
    };

    const swapItems = (i, j) => {
        //Sometimes when swapping items it will create a new empty pti object with only sequencing, this is resulted by some items with a previous sequence after deleted items
        if (i >= 0 && j >= 0 && j <= props.trip.pre_trip_items.length - 1) {
            const update = {
                [`pre_trip_items[${i}].sequence`]: j,
                [`pre_trip_items[${j}].sequence`]: i,
            };
            props.onChange && props.onChange(update);
        }
    };
    const sortedItems = items.sort((a, b) => +a.sequence - +b.sequence);
    const list = sortedItems.map((pti, idx) => (
        <Grid item xs={12} key={`${idx}-${pti.id}`}>
            <PreTripItem disabled={!hasWritePerm} selectable={bulkSelectEnabled} selected={selectedIdxs.includes(idx)} onSelectChange={onSelectChange} key={pti.id} item={pti} idx={idx} isLast={idx === items.length - 1} apiParams={{ tour_id: props.trip.id }} onChange={onItemChange} onDelete={onItemDelete} swapItems={swapItems} onValidate={props.onValidate} trip={props.trip} />
        </Grid>
    ));

    return (
        <>
            {confirmDeleteDialog}
            <ImportItemsFromTripPopper setSyncActive={props.setSyncActive} trip={props.trip} onTripSave={props.onTripSave} onItemAdd={onItemAdd} tripSelectAnchorEl={tripSelectAnchorEl} setTripSelectAnchorEl={setTripSelectAnchorEl} selectedTrips={selectedTrips} setSelectedTrips={setSelectedTrips} />
            <ImportItemsRecordListPopper
                setSyncActive={props.setSyncActive}
                onTripSave={props.onTripSave}
                onItemAdd={onItemAdd}
                trip={props.trip}
                onTripUpdate={props.onChange}
                ItemsRecordListSelectAnchorEl={ItemsRecordListSelectAnchorEl}
                setItemsRecordListSelectAnchorEl={setItemsRecordListSelectAnchorEl}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
            />
            {bulkSelectEnabled && items.length > 0 && (
                <Grid container direction="row" alignItems="center" px={2}>
                    <Grid item container alignItems="center" justifyContent="center" sx={{ flexGrow: 0, flexBasis: '4.166667%', maxWidth: '4.166667%' }}>
                        <Checkbox
                            size="small"
                            sx={{ padding: '0.25rem' }}
                            checked={selectedIdxs.length === items.length}
                            indeterminate={selectedIdxs.length > 0 && selectedIdxs.length < items.length}
                            onChange={onToggleSelectAll}
                            inputProps={{ 'aria-label': 'Select all trip items' }}
                        />
                    </Grid>
                    {/* Same cell geometry as the arrows column so the label starts exactly at the left arrow's left edge. */}
                    <Grid item xs={2} sm={1} container alignItems="center" justifyContent="center">
                        <Box sx={{ width: ARROWS_GROUP_WIDTH }}>
                            <Typography variant="body2" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                                {selectedIdxs.length > 0 ? `${selectedIdxs.length} selected` : 'Select all'}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            )}
            <Grid container direction="row" spacing={0} p={2}>
                {list}
            </Grid>
            {props.trip.pre_trip_items.length > 0 && (
                <Grid item xs={12} my={2}>
                    <Divider />
                </Grid>
            )}
            {hasWritePerm && <AddItemsButtons trip={props.trip} setTripSelectAnchorEl={setTripSelectAnchorEl} setItemsRecordListSelectAnchorEl={setItemsRecordListSelectAnchorEl} selectedCount={selectedIdxs.length} onDeleteSelected={onSelectedDelete} />}
        </>
    );
};

const TripEditItems = (props) => {
    const classes = useStyles();
    const trip = props.data;

    const onPreTripItemDelete = (idx) => {
        props.onChange &&
            props.onChange({
                ...trip,
                pre_trip_items: (trip.pre_trip_items || []).filter((x, i) => i !== idx),
            });
    };

    const onPreTripItemsDelete = (idxs) => {
        const removed = new Set(idxs);
        props.onChange &&
            props.onChange({
                ...trip,
                pre_trip_items: (trip.pre_trip_items || []).filter((x, i) => !removed.has(i)),
            });
    };

    return (
        <div className={classes.root}>
            <TripFlowPreTripList setSyncActive={props.setSyncActive} onTripSave={props.onTripSave} trip={trip} onChange={props.onChange} onDelete={onPreTripItemDelete} onDeleteMany={onPreTripItemsDelete} onValidate={props.onValidate} />
        </div>
    );
};

export default TripEditItems;